/**
 * The single way this application talks to the backend.
 *
 * Everything that must hold for every request lives here — the API prefix,
 * cookie credentials, the CSRF header and the error shape. A component that
 * calls `fetch` directly bypasses all of it, which is why nothing else should.
 */

import { i18n } from '@/shared/i18n';

/** Matches the `servers` entry of api/openapi/gateway.yaml. */
export const API_PREFIX = '/api/v1';

const CSRF_COOKIE = 'csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';
const REQUEST_ID_HEADER = 'X-Request-Id';

/** Methods that cannot change state and therefore need no CSRF token. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Error shape returned by every endpoint, matching the Error schema. */
export interface ApiErrorBody {
  code: string;
  message: string;
  request_id?: string;
  field?: string;
  reason?: string;
}

/**
 * A failed request.
 *
 * Carries the request id so that a user-visible failure can be quoted straight
 * into a bug report and matched against server logs.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly field: string | undefined;
  readonly reason: string | undefined;

  constructor(status: number, body: ApiErrorBody, requestId?: string) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.requestId = body.request_id ?? requestId;
    this.field = body.field;
    this.reason = body.reason;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

/** Reads a cookie the page is allowed to see. */
export function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;

  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(prefix))
    ?.slice(prefix.length);
}

/**
 * Returns the CSRF token, asking the gateway for one when the cookie is absent.
 *
 * Done here rather than by each caller, so that the first mutation of a fresh
 * visit, such as signing in, does not fail with a 403.
 */
async function csrfToken(): Promise<string | undefined> {
  const existing = readCookie(CSRF_COOKIE);
  if (existing !== undefined && existing !== '') {
    return existing;
  }

  await request<unknown>('/csrf');

  return readCookie(CSRF_COOKIE);
}

/**
 * Performs a request against the API.
 *
 * @throws {ApiError} when the response status is not in the 2xx range.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ Accept: 'application/json' });

  const isForm = options.body instanceof FormData;
  if (options.body !== undefined && !isForm) {
    headers.set('Content-Type', 'application/json');
  }

  if (!SAFE_METHODS.has(method)) {
    const token = await csrfToken();
    if (token !== undefined) {
      headers.set(CSRF_HEADER, token);
    }
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    method,
    headers,
    // Sessions live in an HttpOnly cookie, so every request must carry it.
    credentials: 'same-origin',
    body: isForm
      ? (options.body as FormData)
      : options.body === undefined
        ? null
        : JSON.stringify(options.body),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      await readErrorBody(response),
      response.headers.get(REQUEST_ID_HEADER) ?? undefined,
    );
  }

  const text = await response.text();
  if (text === '') {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

/**
 * Reads the error body, falling back to a synthetic one.
 *
 * A gateway timeout or an nginx error page is not JSON, and a client that
 * throws while parsing an error hides the actual failure behind a parse error.
 */
async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>;

    if (typeof body.code === 'string' && typeof body.message === 'string') {
      return body as ApiErrorBody;
    }
  } catch {
    // Falls through to the synthetic body below.
  }

  return {
    code: 'unknown',
    message: i18n.t('errors.generic'),
  };
}
